import { Component } from '@theme/component';

/** Owns comparison selectors and fragment links inside this section's shadow root. */
class KrMowerComparison extends Component {
  #controller;
  #observer;
  #frame;
  #root;

  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    super.disconnectedCallback();
  }

  #bind() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    this.#root = this.querySelector('kr-collection-layout')?.shadowRoot;
    if (!this.#root) {
      this.#observer = new MutationObserver(() => {
        if (this.querySelector('kr-collection-layout')?.shadowRoot) this.#bind();
      });
      this.#observer.observe(this, { childList: true, subtree: true });
      return;
    }
    this.#controller = new AbortController();
    this.#root.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.matches('[data-compare-slot]')) this.#apply();
    }, { signal: this.#controller.signal });
    this.#root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      const preset = event.target.closest('[data-compare-preset]');
      if (preset) {
        const selected = preset.dataset.comparePreset.split(',').filter(Boolean);
        const used = [...selected];
        [...this.#root.querySelectorAll('[data-compare-slot]')].forEach((select, i) => {
          const value = selected[i] ?? [...select.options].find((option) => !used.includes(option.value))?.value;
          if (value) { select.value = value; used.push(value); }
        });
        this.#apply();
        this.#scroll(this.#root.getElementById('palyginimas'));
        return;
      }
      const link = event.target.closest('a[href^="#"]');
      if (!(link instanceof HTMLAnchorElement)) return;
      const target = this.#root.getElementById(link.hash.slice(1));
      if (!target) return;
      event.preventDefault();
      this.#scroll(target);
      history.replaceState(history.state, '', link.hash);
    }, { capture: true, signal: this.#controller.signal });
    window.addEventListener('resize', () => {
      if (this.#frame !== undefined) return;
      this.#frame = requestAnimationFrame(() => { this.#frame = undefined; this.#apply(); });
    }, { passive: true, signal: this.#controller.signal });
    this.#apply();
    const initial = this.#root.getElementById(location.hash.slice(1));
    if (initial) this.#scroll(initial);
  }

  #scroll(target) {
    if (!target) return;
    target.style.scrollMarginTop = `${(document.getElementById('header-group')?.getBoundingClientRect().height ?? 100) + 16}px`;
    target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    target.querySelector('[data-compare-slot]')?.focus({ preventScroll: true });
  }

  #apply() {
    const selects = [...this.#root.querySelectorAll('[data-compare-slot]')];
    const chosen = new Map(selects.map((select) => [select.value, select.dataset.compareSlot]));
    const cells = [...this.#root.querySelectorAll('[data-compare-table] [data-col]')];
    for (const cell of cells) {
      const slot = chosen.get(cell.dataset.col);
      if (slot) { cell.dataset.slot = slot; cell.style.order = slot; }
      else { cell.removeAttribute('data-slot'); cell.style.order = ''; }
    }
    // Keep reading order aligned with the selected visual columns.
    for (const parent of new Set(cells.map((cell) => cell.parentElement))) {
      const current = [...parent.children].filter((cell) => cell.hasAttribute('data-col'));
      const ordered = [...current].sort((a, b) => Number(a.dataset.slot ?? 99) - Number(b.dataset.slot ?? 99));
      if (current.some((cell, i) => cell !== ordered[i])) for (const cell of ordered) parent.append(cell);
    }
    for (const select of selects) for (const option of select.options) option.disabled = option.value !== select.value && chosen.has(option.value);
    const mobile = matchMedia('(max-width: 767px)').matches;
    for (const row of this.#root.querySelectorAll('[data-spec-row]')) {
      const values = [...row.querySelectorAll('[data-col][data-slot]')].filter((cell) => !mobile || cell.dataset.slot !== '3').map((cell) => cell.textContent.replace(/\s+/g, ' ').trim());
      row.toggleAttribute('data-same', values.length > 1 && values.every((value) => value === values[0]));
    }
  }
}
if (!customElements.get('kr-mower-comparison')) customElements.define('kr-mower-comparison', KrMowerComparison);
